import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  getDocs,
  setDoc,
  doc,
  query,
  orderBy,
  where,
  getDoc,
  updateDoc,
  limit,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../utils/init-firebase";
import { getFirebaseAuthorizationHeaders } from "../utils/firebase-auth-headers";
import { loadMetaConversionTrackingModule } from "../../utils/attribution-module-loaders";
import {
  arrayElementLooper,
  trackEvent,
} from "../utils/helper-methods";
import {
  CardData,
  DECK_COLLECTION_NAME,
  DeckObject,
  KeyConcept,
  SummaryOutlineObject,
  USERS_COLLECTION_NAME,
  UserData,
  JUNGLE_BACKEND_PROD_DOMAIN,
  DELETED_DECK_COLLECTION_NAME,
  JUNGLE_CLOUDFLARE_BASE_URL,
} from "../../constants/GlobalTypesAndEnumsAndConstants";
import axios from "axios";
import { updateUser } from "./UserModel";
import { userStore } from "../../zustand/globalStore";
import { addSentryDiagnosticBreadcrumb, trackError } from "../../utils/analytics/track-error";
import {
  DeckLoadError,
  DeckLoadRequestName,
  type DeckLoadRequestObserver,
  type DeckLoadRequestOutcome,
  getDeckLoadTimestampInMilliseconds,
  reportDeckLoadRequestMetrics,
  requestDeckByIdEndpoint,
} from "../utils/deck-load-observability";
import { haveSameParentDeckId } from "./deck-parent";
import { invalidateDeckCardRequests, requestDeckCards } from "../utils/deck-card-requests";
import { prepareDeckArchive } from "../utils/prepare-deck-archive";
import { createModuleLoader, createRetryingModuleLoader } from "../../utils/create-module-loader";

interface GetDeckByIdResponse {
  status: string;
  deck?: DeckObject;
}

export const getDeckById = async (
  deckId: string,
  user: UserData,
  requestObserver?: DeckLoadRequestObserver
) => {
  if (!deckId) {
    return null;
  }
  const endpoint = `${JUNGLE_CLOUDFLARE_BASE_URL}/decks/get_deck_by_id`;
  try {
    const { data, requestAttempts, durationMs, httpStatus, cfRayId, lastRetryError } =
      await requestDeckByIdEndpoint<GetDeckByIdResponse>({
        url: endpoint,
        body: {
          user_id: user?.id,
          deck_id: deckId,
        },
      });

    if (requestAttempts > 1) {
      // Recovered transport failures are supporting context for a later
      // terminal error, not standalone user-visible failures.
      addSentryDiagnosticBreadcrumb({
        category: "deck-load",
        message: "Deck load recovered after retry",
        data: {
          deckId,
          endpoint,
          requestAttempts,
          durationMs,
          errorCode: lastRetryError?.code ?? "unknown",
          httpStatus: lastRetryError?.httpStatus,
          cfRayId: lastRetryError?.cfRayId ?? cfRayId,
          isOnline: globalThis.navigator?.onLine,
          documentVisibility: globalThis.document?.visibilityState,
        },
      });
    }

    if (data.status === "success" && data.deck) {
      reportDeckLoadRequestMetrics(requestObserver, {
        requestName: DeckLoadRequestName.GetDeckById,
        durationMs,
        outcome: "success",
        requestAttempts,
        httpStatus,
        cfRayId,
      });
      return data.deck;
    }

    reportDeckLoadRequestMetrics(requestObserver, {
      requestName: DeckLoadRequestName.GetDeckById,
      durationMs,
      outcome: "backend-rejected",
      requestAttempts,
      httpStatus,
      cfRayId,
    });

    // This can intentionally accompany the recovered-transport warning above:
    // reaching the backend and receiving an unusable response are separate failures.
    trackError(new Error("Deck endpoint did not return a deck"), {
      scope: "deck-load",
      userId: user?.id,
      tags: {
        deck_load_outcome: "backend-rejected",
        deck_load_error_code: "backend-response",
        deck_id: deckId,
        deck_load_request_attempts: String(requestAttempts),
        http_status: String(httpStatus),
        cf_ray_id: cfRayId ?? "missing",
      },
      extra: {
        deckId,
        endpoint,
        requestAttempts,
        durationMs,
        httpStatus,
        responseStatus: data.status,
        cfRayId,
        sentryOrigin: "frontend",
      },
    });
  } catch (error) {
    const deckLoadError = error instanceof DeckLoadError ? error : null;
    reportDeckLoadRequestMetrics(requestObserver, {
      requestName: DeckLoadRequestName.GetDeckById,
      durationMs: deckLoadError?.durationMs ?? 0,
      outcome: "failed",
      requestAttempts: deckLoadError?.requestAttempts ?? 1,
      httpStatus: deckLoadError?.httpStatus,
      cfRayId: deckLoadError?.cfRayId,
    });
    trackError(error, {
      scope: "deck-load",
      userId: user?.id,
      tags: {
        deck_load_outcome: "failed",
        deck_load_error_code: deckLoadError?.code ?? "unknown",
        deck_id: deckId,
        deck_load_request_attempts: String(deckLoadError?.requestAttempts ?? "unknown"),
        http_status: String(deckLoadError?.httpStatus ?? "missing"),
        cf_ray_id: deckLoadError?.cfRayId ?? "missing",
      },
      extra: {
        deckId,
        endpoint,
        durationMs: deckLoadError?.durationMs,
        requestAttempts: deckLoadError?.requestAttempts,
        retryable: deckLoadError?.retryable,
        httpStatus: deckLoadError?.httpStatus,
        cfRayId: deckLoadError?.cfRayId,
        sentryOrigin: "frontend",
        isOnline: globalThis.navigator?.onLine,
        documentVisibility: globalThis.document?.visibilityState,
      },
    });
  }

  return null;
};

/** Archive/unarchive through the existing deactivate/activate endpoints, which flip the deck and its sub-decks.
 * Resolves to whether the deck now has the requested state; a failure the deck's own state can't settle rejects.
 *
 * The deployed endpoint writes each deck and then that deck's per-user copy, and stops with an error at the first
 * sub-deck that has no copy (older sub-decks often don't): the decks before it changed, the rest did not, and the
 * learner saw "it didn't work" although the class itself had changed (owner, 2026-10-29). After such an error each
 * sub-deck is flipped on its own, so every one is reached, and the deck is read back to decide. */
export const setDeckAndSubdecksActive = async (deckId: string, userId: string, isActive: boolean, signal?: AbortSignal): Promise<boolean> => {
  if (!isActive) await prepareDeckArchive(deckId, userId, signal);
  const flip = async (id: string) => {
    const { data } = await axios.post(`${JUNGLE_CLOUDFLARE_BASE_URL}/decks/make_deck_and_subdecks_${isActive ? "active" : "inactive"}`,
      { deck_id: id, user_id: userId }, { signal });
    return data?.status === "success";
  };
  try { if (await flip(deckId)) return true; }
  catch (error) { if (signal?.aborted) throw error; }
  const owner = { id: userId } as UserData;
  const finish = async (id: string, depth: number): Promise<void> => {
    const deck = await getDeckById(id, owner);
    for (const childId of Array.isArray(deck?.children_deck_ids) ? deck.children_deck_ids as string[] : []) {
      signal?.throwIfAborted();
      const done = await flip(childId).catch(() => false);
      if (!done && depth < 8) await finish(childId, depth + 1);
    }
  };
  await finish(deckId, 0);
  const deck = await getDeckById(deckId, owner);
  if (!deck) throw new Error("deck_state_unknown");
  return (deck.is_active !== false) === isActive;
};

export const createDeck = async (deckObjectToCreate: DeckObject, user: UserData) => {
  const decksCollectionRef = collection(db, `${DECK_COLLECTION_NAME}`);

  const q = query(
    decksCollectionRef,
    where("name", "==", deckObjectToCreate.name),
    where("user_id", "==", deckObjectToCreate.user_id)
  );
  const decksDocs = await getDocs(q);
  const decksSaved = arrayElementLooper(decksDocs.docs);

  if (decksSaved.length > 0) {
    // Check if a deck with the same name exists within the parent deck.
    let decks_with_same_deck_parent_id = decksSaved.filter((deck: DeckObject) =>
      haveSameParentDeckId(deck.parent_deck_id, deckObjectToCreate.parent_deck_id)
    );
    if (decks_with_same_deck_parent_id.length > 0) {
      return decks_with_same_deck_parent_id[0];
    }
  }

  deckObjectToCreate.date_created = new Date();
  const deckObject = await addDoc(decksCollectionRef, deckObjectToCreate);

  if (deckObjectToCreate.parent_deck_id) {
    const parentDeck = await getDeckById(deckObjectToCreate.parent_deck_id, user);
    if (parentDeck) {
      let children_deck_ids = parentDeck.children_deck_ids ? parentDeck.children_deck_ids : [];
      children_deck_ids.push(deckObject.id);
      parentDeck.children_deck_ids = children_deck_ids;
      updateDeck(deckObjectToCreate.parent_deck_id, deckObjectToCreate.user_id, {
        children_deck_ids: children_deck_ids,
      });
    }
  }
  deckObjectToCreate.id = deckObject.id;
  // A new root deck is a class (or a non-student's folder): report its subject, never its name. Loaded on demand:
  // the reporter imports helper-methods, which imports this module.
  if (!deckObjectToCreate.parent_deck_id) {
    const createdDeck = { ...deckObjectToCreate };
    void loadClassSubjectAnalyticsModule().then(module => module.reportClassSubjectForDeck(createdDeck, user, "created")).catch(() => {});
  }
  return deckObjectToCreate;
};

export const updateDeck = async (deckId: string, userId: string, dataToUpdate) => {
  if (!deckId || !userId) return;

  await setDoc(doc(db, `${DECK_COLLECTION_NAME}`, deckId), dataToUpdate, {
    merge: true,
  });

  await setDoc(doc(db, `${USERS_COLLECTION_NAME}/${userId}/${DECK_COLLECTION_NAME}`, deckId), dataToUpdate, {
    merge: true,
  });
  return true;
};

/** Firestore: whether the deck document exists in the top-level decks collection. */
export const doesDeckDocumentExistInFirestore = async (deckId: string): Promise<boolean> => {
  if (!deckId) return false;
  const deckRef = doc(db, DECK_COLLECTION_NAME, deckId);
  const deckSnapshot = await getDoc(deckRef);
  return deckSnapshot.exists();
};

/** Firestore: append a live recording id to `live_recording_ids` (ArrayUnion dedupes). */
export const addLiveRecordingIdToDeckDocument = async (deckId: string, recordingId: string): Promise<void> => {
  const deckRef = doc(db, DECK_COLLECTION_NAME, deckId);
  await updateDoc(deckRef, {
    live_recording_ids: arrayUnion(recordingId),
  });
};

/** Firestore: remove a live recording id from `live_recording_ids`. */
export const removeLiveRecordingIdFromDeckDocument = async (deckId: string, recordingId: string): Promise<void> => {
  const deckRef = doc(db, DECK_COLLECTION_NAME, deckId);
  await updateDoc(deckRef, {
    live_recording_ids: arrayRemove(recordingId),
  });
};

export const deleteDeck = async (deckObjectToDelete: DeckObject, user: any) => {
  if (!deckObjectToDelete.id) return;
  let wasDeleted = false;
  invalidateDeckCardRequests(deckObjectToDelete.id);
  await fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/decks/delete_deck`, {
    method: "POST",
    // The Worker requires the deck owner's Firebase ID token (requireMatchingFirebaseUser); without it every delete is 401.
    headers: {
      "Content-Type": "application/json",
      ...(await getFirebaseAuthorizationHeaders().catch(() => ({}))),
    },
    body: JSON.stringify({
      user_id: user?.id,
      deck_id: deckObjectToDelete.id,
    }),
  })
    .then(async (response) => {
      invalidateDeckCardRequests(deckObjectToDelete.id);
      return response.json();
    })
    .then(async (response) => {
      if (response.status === "success") {
        userStore.getState().deleteDeckFromUserDecks(deckObjectToDelete.id);
        wasDeleted = true;
        trackEvent(
          "Deleted Deck",
          {
            deckId: deckObjectToDelete?.id,
            deckName: deckObjectToDelete?.name,
          },
          user
        );
      } else {
        trackEvent(
          "Error: Failed to Deck Deleted",
          {
            deckId: deckObjectToDelete?.id,
            deckName: deckObjectToDelete?.name,
          },
          user
        );
      }
    })
    .catch((error) => {
      console.error("Error to Flashcard Deleted:", error);
      trackEvent(
        "Error: Failed to Deck Deleted",
        {
          deckName: deckObjectToDelete?.name,
        },
        user
      );
    });
  return wasDeleted;
};

export const getAllDecksForUserId = async (
  userId: string,
  shouldFilterForActiveDecks: boolean = true, // we can also user this filter to include children decks
  shouldIncludeSharedClassDecks: boolean = false,
  shouldSortDeckByDate: boolean = true
) => {
  const decksCollectionRef = collection(db, DECK_COLLECTION_NAME);
  const q = query(decksCollectionRef, where("user_id", "==", userId));
  const savedDecksDocs = await getDocs(q);
  let savedDecks = arrayElementLooper(savedDecksDocs.docs);

  if (shouldIncludeSharedClassDecks) {
    const userDocRef = doc(db, USERS_COLLECTION_NAME, userId);
    const userDoc = await getDoc(userDocRef);
    const userData = userDoc.data();

    if (userData && userData.class_properties && userData.class_properties.class_decks_id) {
      const classDeckIds = userData.class_properties.class_decks_id;
      const classDeckPromises = classDeckIds.map(async (deckId) => {
        const deckDocRef = doc(db, DECK_COLLECTION_NAME, deckId);
        const deckDoc = await getDoc(deckDocRef);
        return deckDoc.exists() ? { id: deckDoc.id, ...deckDoc.data() } : null;
      });
      const classDecks = await Promise.all(classDeckPromises);
      savedDecks = [...savedDecks, ...classDecks.filter((deck) => deck !== null)];
    }
  }

  if (shouldFilterForActiveDecks) {
    savedDecks = savedDecks.filter((deck: DeckObject) => deck.is_active);
  }

  if (shouldSortDeckByDate) {
    savedDecks.forEach((deck) => {
      if (!deck.last_card_saved_to_deck_date) {
        deck.date_to_use_for_sorting = deck.date_created;
      } else {
        deck.date_to_use_for_sorting = deck.last_card_saved_to_deck_date;
      }
    });

    savedDecks.sort((a, b) => {
      try {
        const dateA = a.date_to_use_for_sorting
          ? typeof a.date_to_use_for_sorting === "string"
            ? new Date(a.date_to_use_for_sorting)
            : new Date(a.date_to_use_for_sorting.seconds * 1000 + a.date_to_use_for_sorting.nanoseconds / 1000000)
          : new Date(0);
        const dateB = b.date_to_use_for_sorting
          ? typeof b.date_to_use_for_sorting === "string"
            ? new Date(b.date_to_use_for_sorting)
            : new Date(b.date_to_use_for_sorting.seconds * 1000 + b.date_to_use_for_sorting.nanoseconds / 1000000)
          : new Date(0);
        return dateB.getTime() - dateA.getTime();
      } catch (e) {
        return 0;
      }
    });

    // remove date_to_use_for_sorting field
    savedDecks.forEach((deck) => {
      delete deck.date_to_use_for_sorting;
    });
  }

  return savedDecks;
};

export const getAllRootDecksForUserId = async (userId: string, shouldIncludeClassDecks: boolean = false) => {
  return await getAllDecksOrAllSubdecksOfADeckForUserId(userId, null, shouldIncludeClassDecks);
};

export const getAllSubdecksOfADeckForDeckAndUserId = async (userId: string, deck?: DeckObject) => {
  return await getAllDecksOrAllSubdecksOfADeckForUserId(userId, deck);
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// deck is optional, when not passed, it will get all root decks
const getAllDecksOrAllSubdecksOfADeckForUserId = async (
  userId: string,
  deck?: DeckObject,
  shouldIncludeClassDecks: boolean = false
): Promise<DeckObject[]> => {
  const params = {
    user_id: userId,
    deck_id: deck?.id,
    should_include_class_decks: shouldIncludeClassDecks,
  };

  // The browser's offline hint can be false while the API is reachable.
  // Let the request and its existing retries determine whether decks can load.

  // NOTE: there used to be a "fall back to the Python backend after 2 failed
  // retries" branch here, but the Python route was removed when this endpoint
  // went worker-native — the fallback had been 404ing for months. All retries
  // now target the worker (removed 2026-06-11).
  // The account pilot runs the updated deck projection locally; production's
  // older projection omits saved class appearance and would overwrite it.
  const localTutorAccount = import.meta.env.DEV && import.meta.env.VITE_LOCAL_TUTOR_PRODUCTION_ACCOUNT === "wisdolia";
  const endpoint = `${localTutorAccount ? "" : JUNGLE_CLOUDFLARE_BASE_URL}/decks/get_decks_for_user`;
  const maxRetries = 3;
  let currentRetry = 0;
  let lastError: any = null;

  while (currentRetry <= maxRetries) {
    try {
      const { data } = await axios.post(endpoint, params, {
        headers: { "Content-Type": "application/json", ...(localTutorAccount ? await getFirebaseAuthorizationHeaders() : {}) },
        // Allow slow or cold services to finish while still bounding a stalled
        // request. A five-second cutoff repeatedly abandoned valid class loads.
        timeout: 30_000,
      });

      if (data.status === "success") {
        return data.decks as DeckObject[];
      } else {
        // Treat non-success status as a failure for retry purposes
        lastError = new Error(`Server responded with status: ${data.status}`);
        trackEvent("Error: Failed to get decks for user - Non-success status", {
          endpoint: endpoint,
          userId,
          paramsSent: JSON.stringify(params),
          serverResponse: JSON.stringify(data),
          attempt: currentRetry + 1,
        });
        // Don't immediately throw, allow retry logic below
      }
    } catch (error: any) {
      lastError = error; // Store the error for potential final throw
      let errorMessage = "Unknown error";
      let errorType = "GenericError";

      if (error.response) {
        errorMessage = `Server Error: ${error.response.status} - ${error.response.statusText}`;
        errorType = "ServerError";
      } else if (error.request) {
        errorMessage = "Load failed: No response from server";
        errorType = "NetworkError";
      } else {
        errorMessage = error.message || "Unexpected error occurred";
        errorType = "UnknownError";
      }

      trackEvent(`Error: Failed to get decks for user - Attempt ${currentRetry + 1}`, {
        endpoint: endpoint,
        userId,
        paramsSent: JSON.stringify(params),
        endpointUsed: endpoint,
        errorMessage,
        errorType,
        errorResponse: error.response ? JSON.stringify(error.response.data) : undefined,
        errorStack: error.stack || "No stack trace",
        attempt: currentRetry + 1,
      });
    }

    // If we are here, it means the request failed (either caught exception or non-success status)
    if (currentRetry < maxRetries) {
      const delayDuration = Math.pow(2, currentRetry) * 1000; // Exponential backoff: 1s, 2s
      await delay(delayDuration);
      currentRetry++;
    } else {
      // Max retries reached, capture the last error and throw
      throw lastError || new Error("Failed to get decks for user after multiple attempts");
    }
  }
  // This part should theoretically be unreachable due to the throw in the else block
  throw new Error("Unexpected state reached in getAllDecksOrAllSubdecksOfADeckForUserId");
};

// deck is optional, when not passed, it will get all root decks
// const getAllDecksOrAllSubdecksOfADeckForUserId = async (
//   userId: string,
//   deck?: DeckObject,
//   shouldIncludeClassDecks: boolean = false
// ): Promise<DeckObject[]> => {
//   const params = {
//     user_id: userId,
//     deck_id: deck?.id,
//     should_include_class_decks: shouldIncludeClassDecks,
//   };
//   if (!navigator.onLine) {
//     trackEvent("Error: No internet connection", {
//       userId,
//       paramsSent: JSON.stringify(params),
//     });
//   }
//   const endpoint = `${JUNGLE_BACKEND_PROD_DOMAIN}/decks/get_decks_for_user`;
//   try {
//     const { data } = await axios.post(endpoint, params, {
//       headers: { "Content-Type": "application/json" },
//     });

//     if (data.status === "success") {
//       return data.decks as DeckObject[];
//     } else {
//       trackEvent("Error: Failed to get decks for user", {
//         endpoint: endpoint,
//         userId,
//         paramsSent: JSON.stringify(params),
//         serverResponse: JSON.stringify(data),
//       });
//       throw new Error("Failed to get decks for user");
//     }
//   } catch (error: any) {
//     let errorMessage = "Unknown error";
//     let errorType = "GenericError";

//     if (error.response) {
//       // Server responded with an error status (4xx or 5xx)
//       errorMessage = `Server Error: ${error.response.status} - ${error.response.statusText}`;
//       errorType = "ServerError";
//     } else if (error.request) {
//       // No response received from the server (network issue or timeout)
//       errorMessage = "Load failed: No response from server";
//       errorType = "NetworkError";
//     } else {
//       // Unexpected error
//       errorMessage = error.message || "Unexpected error occurred";
//       errorType = "UnknownError";
//     }

//     trackEvent(`Error: Failed to get decks for user`, {
//       endpoint: endpoint,
//       userId,
//       paramsSent: JSON.stringify(params),
//       errorMessage,
//       errorType,
//       errorResponse: error.response,
//       errorStack: error.stack || "No stack trace",
//     });
//     throw new Error(errorMessage);
//   }
// };

export const getDeckByNameAndUserId = async (
  deckName: string,
  userId: string | null,
  parent_deck_id?: string | null
): Promise<DeckObject | null> => {
  if (!userId) return null;

  let deckObject: DeckObject | null = null;

  await fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/decks/get_deck_by_name_and_user_id`, {
    method: "POST",
    body: JSON.stringify({
      deck_name: deckName,
      user_id: userId,
      parent_deck_id: parent_deck_id,
    }),
    headers: {
      "Content-Type": "application/json",
    },
  })
    .then(async (response) => {
      return response.json();
    })
    .then(async (response) => {
      if (response.status === "success") {
        deckObject = response.deck;
      }
    });
  return deckObject;
};

/** The same lookup, but a failed request throws instead of reading as "no such deck". A rename's duplicate check
 * must not let a name through because the check itself failed (a slow or failing request used to look like
 * "no class has this name", and the rename was written). */
export const getDeckByNameAndUserIdOrThrow = async (
  deckName: string,
  userId: string,
  parent_deck_id?: string | null
): Promise<DeckObject | null> => {
  const response = await fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/decks/get_deck_by_name_and_user_id`, {
    method: "POST",
    body: JSON.stringify({ deck_name: deckName, user_id: userId, parent_deck_id: parent_deck_id }),
    headers: { "Content-Type": "application/json" },
  });
  const body = response.ok ? await response.json().catch(() => null) : null;
  if (!body || body.status !== "success") throw new Error(`deck name lookup failed (${response.status})`);
  return body.deck ?? null;
};

// export const getDeckByNameAndUserId = async (deckName: string, userId: string) => {
//   const decksCollectionRef = collection(db, `${USERS_COLLECTION_NAME}/${userId}/${DECK_COLLECTION_NAME}`);

//   const decksSavedDocks = await getDocs(
//     query(decksCollectionRef, where("name", "==", deckName), orderBy("date_created", "desc"))
//   );
//   const decksSaved = arrayElementLooper(decksSavedDocks.docs);
//   let deck = decksSaved.length > 0 ? decksSaved[0] : null;

//   return deck;
// };

export const getAllCardsForDeckAndSubdecks = async (
  deck_id: string,
  user: any,
  throwOnError = false,
  requestObserver?: DeckLoadRequestObserver,
  cardSetRevision?: string
) => {
  let allCardsForDeck: Array<CardData> = [];
  let allCardsForCurrentDeckAndSubdecks: Array<CardData> = [];
  let resolvedDeckId = deck_id;
  const endpoint = `${JUNGLE_CLOUDFLARE_BASE_URL}/cards/get_all_cards_data_for_deck_and_subdecks`;
  const requestStartedAtMs = getDeckLoadTimestampInMilliseconds();
  let requestOutcome: DeckLoadRequestOutcome = "failed";
  let httpStatus: number | undefined;
  let cfRayId: string | undefined;

  const pendingRequest = requestDeckCards(endpoint, deck_id, user?.id, cardSetRevision);
  try {
    const response = await pendingRequest.request;
    httpStatus = response.httpStatus;
    cfRayId = response.cfRayId;
    const responseData = response.responseData;

    if (responseData.status === "success") {
      requestOutcome = "success";
      resolvedDeckId = responseData.resolved_deck_id ?? responseData.all_cards_for_deck?.[0]?.deck_id ?? deck_id;
      allCardsForDeck = responseData.all_cards_for_deck;
      allCardsForCurrentDeckAndSubdecks = responseData.all_cards_for_deck_and_subdecks;

      if (allCardsForCurrentDeckAndSubdecks) {
        // when the user is not the owner of the deck, we need to remove the answers and ratings
        if (allCardsForCurrentDeckAndSubdecks[0]?.user_id !== user?.id) {
          allCardsForCurrentDeckAndSubdecks = allCardsForCurrentDeckAndSubdecks.map((card) => {
            card.answers_from_user_and_ai_feedback = [];
            card.knowledge_understanding_ratings = [];
            return card;
          });
          allCardsForDeck = allCardsForDeck.map((card) => {
            card.answers_from_user_and_ai_feedback = [];
            card.knowledge_understanding_ratings = [];
            return card;
          });
        }
      }
    } else {
      requestOutcome = "backend-rejected";
      if (throwOnError) throw new Error("Failed to load cards for review");
      trackError(new Error("Cards endpoint did not return deck data"), {
        scope: "deck-load-cards",
        userId: user?.id,
        tags: {
          deck_load_outcome: requestOutcome,
          deck_id: deck_id,
          http_status: String(httpStatus),
          cf_ray_id: cfRayId ?? "missing",
        },
        extra: { deckId: deck_id, endpoint, responseStatus: responseData.status, httpStatus, cfRayId },
      });
    }
  } catch (error) {
    if (throwOnError) throw error;
    requestOutcome = "failed";
    trackError(error, {
      scope: "deck-load-cards",
      userId: user?.id,
      tags: {
        deck_load_outcome: requestOutcome,
        deck_id: deck_id,
        http_status: String(httpStatus ?? "missing"),
        cf_ray_id: cfRayId ?? "missing",
      },
      extra: { deckId: deck_id, endpoint, httpStatus, cfRayId },
    });
  } finally {
    reportDeckLoadRequestMetrics(requestObserver, {
      requestName: DeckLoadRequestName.GetAllCardsForDeckAndSubdecks,
      durationMs: Math.max(0, Math.round(getDeckLoadTimestampInMilliseconds() - requestStartedAtMs)),
      outcome: requestOutcome,
      requestAttempts: pendingRequest.requestAttempts,
      httpStatus,
      cfRayId,
    });
  }
  return {
    allCardsForDeck,
    allCardsForCurrentDeckAndSubdecks,
    resolvedDeckId,
  };
};

export const getAllDataForDeckAndSubdecks = async (
  deck_id: string,
  user: any,
  setSummaryOutlinesForDeckAndSubdecks: any,
  setKeyConcepts: any,
  setCurrentKeyConcept: any,
  requestObserver?: DeckLoadRequestObserver
) => {
  let allSummaryOutlinesForDeckAndSubdecks: Array<SummaryOutlineObject> = [];
  let allKeyConceptsForDeckAndSubdecks: Array<KeyConcept> = [];
  const endpoint = `${JUNGLE_BACKEND_PROD_DOMAIN}/cards/get_all_data_for_deck_and_subdecks/${deck_id}`;
  const requestStartedAtMs = getDeckLoadTimestampInMilliseconds();
  let requestOutcome: DeckLoadRequestOutcome = "failed";
  let httpStatus: number | undefined;
  let cfRayId: string | undefined;

  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        user_id: user?.id,
      }),
    });
    httpStatus = response.status;
    cfRayId = response.headers?.get("cf-ray") || undefined;
    const responseData = await response.json();

    if (responseData.status === "success") {
      requestOutcome = "success";
      allSummaryOutlinesForDeckAndSubdecks = responseData.all_summary_outlines_for_deck_and_subdecks;
      allKeyConceptsForDeckAndSubdecks = responseData.all_key_concepts_for_deck_and_subdecks;

      if (allSummaryOutlinesForDeckAndSubdecks) {
        setSummaryOutlinesForDeckAndSubdecks(allSummaryOutlinesForDeckAndSubdecks);
      }
      if (allKeyConceptsForDeckAndSubdecks) {
        allKeyConceptsForDeckAndSubdecks.sort((a, b) => a.index - b.index);
        setCurrentKeyConcept(allKeyConceptsForDeckAndSubdecks[0]);
        setKeyConcepts(allKeyConceptsForDeckAndSubdecks);
      }
    } else {
      requestOutcome = "backend-rejected";
      trackError(new Error("Deck summary endpoint did not return data"), {
        scope: "deck-load-summary-data",
        userId: user?.id,
        tags: {
          deck_load_outcome: requestOutcome,
          deck_id: deck_id,
          http_status: String(httpStatus),
          cf_ray_id: cfRayId ?? "missing",
        },
        extra: { deckId: deck_id, endpoint, responseStatus: responseData.status, httpStatus, cfRayId },
      });
    }
  } catch (error) {
    requestOutcome = "failed";
    trackError(error, {
      scope: "deck-load-summary-data",
      userId: user?.id,
      tags: {
        deck_load_outcome: requestOutcome,
        deck_id: deck_id,
        http_status: String(httpStatus ?? "missing"),
        cf_ray_id: cfRayId ?? "missing",
      },
      extra: { deckId: deck_id, endpoint, httpStatus, cfRayId },
    });
  } finally {
    reportDeckLoadRequestMetrics(requestObserver, {
      requestName: DeckLoadRequestName.GetAllDataForDeckAndSubdecks,
      durationMs: Math.max(0, Math.round(getDeckLoadTimestampInMilliseconds() - requestStartedAtMs)),
      outcome: requestOutcome,
      requestAttempts: 1,
      httpStatus,
      cfRayId,
    });
  }

  return {
    allSummaryOutlinesForDeckAndSubdecks,
    allKeyConceptsForDeckAndSubdecks,
  };
};

export const assignUserIdToNewDeckAndIncrementNumberOfSetsOfCardsGenerated = async (
  user: UserData,
  currentReviewDeckId: string,
  numOfCardsInDeck: number,
  contextFromDeckToDetermineDeckNameToSaveTo: string,
  // False when the deck is a copy of someone else's shared deck: saving it is not a generation.
  { shouldReportMetaActivation = true }: { shouldReportMetaActivation?: boolean } = {}
) => {
  let statusResponseOfAssignUserToNewDeck = await axios.post(
    `${JUNGLE_BACKEND_PROD_DOMAIN}/decks/assign_user_id_to_new_deck`,
    {
      user_id: user?.id,
      deck_id: currentReviewDeckId,
      context_from_deck_to_determine_deck_name_to_save_to: contextFromDeckToDetermineDeckNameToSaveTo,
    }
  );

  if (user && (!user?.number_of_sets_of_cards_generated || user?.number_of_sets_of_cards_generated === 0)) {
    updateUser(user?.id, { number_of_sets_of_cards_generated: 1 });
    // A set generated before signup becomes the account's first: Meta activation (Lead). The reporter loads
    // after startup; it is fire-and-forget.
    if (shouldReportMetaActivation) {
      void loadMetaConversionTrackingModule()
        .then(({ reportMetaActivation }) => reportMetaActivation({ userId: user?.id, deckId: currentReviewDeckId }))
        .catch(() => {});
    }
  }
  updateDeck(currentReviewDeckId, user?.id, { last_date_viewed: new Date() });
  return statusResponseOfAssignUserToNewDeck?.data;
};

// The save loop is loaded on first use to keep it out of the startup bundle; the review page
// prefetches it, so an answer given offline normally finds it already loaded.
export const loadDeckResumePositionModule = createModuleLoader(() => import("./deck-resume-position"));
const loadClassSubjectAnalyticsModule = createModuleLoader(() => import("../../features/class-appearance/classSubjectAnalytics"));
// Pending answers share automatic recovery when the prefetched module was unavailable.
const loadDeckResumePositionModuleWithRecovery = createRetryingModuleLoader(loadDeckResumePositionModule);

const advanceLastCardAnsweredIndexForUser = (deckId: string, userId: string, answeredIndex: number) => {
  loadDeckResumePositionModuleWithRecovery()
    .then(({ advanceLastCardAnsweredIndexForUser: advance }) => advance(deckId, userId, answeredIndex))
    .catch(() => {});
};

export const updateLastCardAnsweredIndexForDeckAndParentDeck = async (
  user: UserData,
  currentReviewDeck: DeckObject,
  setCurrentReviewDeck: (deck: DeckObject) => void,
  indexOfCurrentCard: number,
  currentCardData: CardData,
  numOfCardsInDeck: number
) => {
  // Add safety checks to prevent undefined errors
  if (!user?.id || !currentReviewDeck?.id || !currentCardData) {
    console.warn("Missing required data for updateLastCardAnsweredIndexForDeckAndParentDeck", {
      userId: user?.id,
      currentReviewDeckId: currentReviewDeck?.id,
      currentCardData: !!currentCardData,
    });
    return;
  }

  const lastCardAnsweredIndexByUser = currentReviewDeck?.last_card_answered_index_by_user?.[user.id];

  // skip if index of card is less than the last card answered index by the user
  if (indexOfCurrentCard <= lastCardAnsweredIndexByUser) return;

  // update the last card answered index for the current deck and the parent deck if it exists.
  // Not awaited, like the previous direct writes, so a slow network never delays answering or exiting.
  advanceLastCardAnsweredIndexForUser(currentReviewDeck.id, user.id, indexOfCurrentCard);

  // If the card belongs to a child deck of the current deck, also advance the child deck's pointer.
  // That pointer is a position in the child deck's own resume order, so use the card's index in
  // that order; the generation-time card_index is a different ordering and would jump the child's
  // resume position. A child that has never built its own order starts from its first card.
  if (currentCardData.deck_id && currentReviewDeck.id !== currentCardData.deck_id) {
    const cardIndexInChildDeckOrder = currentCardData.deck_id_to_card_index?.[currentCardData.deck_id];
    if (cardIndexInChildDeckOrder !== undefined) {
      advanceLastCardAnsweredIndexForUser(currentCardData.deck_id, user.id, cardIndexInChildDeckOrder);
    }
  }

  // update state
  setCurrentReviewDeck({
    ...currentReviewDeck,
    last_card_answered_index_by_user: {
      ...currentReviewDeck.last_card_answered_index_by_user,
      [user.id]: indexOfCurrentCard,
    },
  });
};

// Load the review algorithm when a session needs it, retaining pending callers through outages.
const loadDeckReviewPreparationModule = createRetryingModuleLoader(() => import("./deck-review-preparation"));
export const prepareCardsDataForReview: typeof import("./deck-review-preparation").prepareCardsDataForReview = async (
  params
) => {
  const reviewPreparation = await loadDeckReviewPreparationModule();
  return reviewPreparation.prepareCardsDataForReview(params);
};

export const getRootDeckIdForDeckId = async (deckId: string): Promise<string> => {
  try {
    let currentDeckId = deckId;
    const visitedDeckIds = new Set<string>(); // To prevent infinite loops in case of circular references

    while (currentDeckId && !visitedDeckIds.has(currentDeckId)) {
      visitedDeckIds.add(currentDeckId);

      const deckRef = doc(db, DECK_COLLECTION_NAME, currentDeckId);
      const deckDoc = await getDoc(deckRef);
      const deckData = deckDoc.data();

      if (!deckData) {
        return currentDeckId; // Return the last valid deck ID if data is missing
      }

      const parentDeckId = deckData.parent_deck_id;

      if (!parentDeckId) {
        return currentDeckId; // This is the root deck
      }

      currentDeckId = parentDeckId;
    }

    // If we exit the loop due to a circular reference, return the last deck ID
    return currentDeckId;
  } catch (e) {
    console.error("Error getting root deck ID:", e);
    return deckId; // Return the original deck ID in case of error
  }
};

/** A deck's name, read from its own document only (no subdecks or parents), or null. */
export const getDeckNameById = async (deckId: string): Promise<string | null> => {
  if (!deckId) return null;
  try {
    const deckDoc = await getDoc(doc(db, DECK_COLLECTION_NAME, deckId));
    const deckName = deckDoc.data()?.name;
    return typeof deckName === "string" ? deckName : null;
  } catch {
    return null;
  }
};

export const getGetLastDeletedDecksForUser = async (userId: string): Promise<DeckObject[]> => {
  const decksCollectionRef = collection(db, DELETED_DECK_COLLECTION_NAME);

  const q = query(
    decksCollectionRef,
    where("user_id", "==", userId),
    where("is_deck_generated_from_document", "==", true),
    orderBy("date_created", "desc"),
    limit(20)
  );

  const querySnapshot = await getDocs(q);
  const deletedDecks: DeckObject[] = querySnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as DeckObject[];

  return deletedDecks;
};

export const getGetLastGeneratedDecksForUser = async (userId: string): Promise<DeckObject[]> => {
  const decksCollectionRef = collection(db, DECK_COLLECTION_NAME);

  // Only fetch decks generated from a document
  const q = query(
    decksCollectionRef,
    where("user_id", "==", userId),
    where("is_deck_generated_from_document", "==", true),
    orderBy("date_created", "desc"),
    limit(20)
  );

  const querySnapshot = await getDocs(q);
  const generatedDecks: DeckObject[] = querySnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as DeckObject[];

  return generatedDecks;
};

export const getNumberOfSetsOfCardsGeneratedForUser = async (userId: string): Promise<number> => {
  const decksCollectionRef = collection(db, DECK_COLLECTION_NAME);
  const q = query(
    decksCollectionRef,
    where("user_id", "==", userId),
    where("is_deck_generated_from_document", "==", true)
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.size;
};

export const joinClassForUserFunction = async (deckId: string, userId: string) => {
  if (!deckId || !userId) {
    return;
  }

  const deckRef = doc(db, DECK_COLLECTION_NAME, deckId);
  const deckSnapshot = await getDoc(deckRef);
  const deckData = deckSnapshot.data() as DeckObject | undefined;

  if (!deckData) {
    return;
  }

  const deckOwnerUserId = deckData.user_id;

  // If user is trying to join their own class, then don't do anything
  if (deckOwnerUserId && deckOwnerUserId === userId) {
    return;
  }

  const userDocRef = doc(db, USERS_COLLECTION_NAME, userId);
  const userSnapshot = await getDoc(userDocRef);
  const userData = userSnapshot.data() as UserData | undefined;

  if (!userData) {
    return;
  }

  let userDeckRef: ReturnType<typeof doc> | null = null;

  if (deckOwnerUserId) {
    userDeckRef = doc(db, `${USERS_COLLECTION_NAME}/${deckOwnerUserId}/${DECK_COLLECTION_NAME}`, deckId);
  }

  // 1) Set deck as class
  const isDeckAClass = deckData.is_deck_a_class ?? false;

  if (!isDeckAClass) {
    trackEvent(
      "New Class Created",
      {
        classDeckId: deckId,
        deckName: deckData.name || "Undefined",
        userId: userId,
        userType: userData.user_type || "Undefined",
        studentType: userData.student_type || "Undefined",
      },
      userData
    );

    await updateDoc(deckRef, { is_deck_a_class: true });

    if (userDeckRef) {
      await updateDoc(userDeckRef, { is_deck_a_class: true });
    }
  }

  // 2) Add user to class (user document)
  const userClassProperties = {
    ...(userData.class_properties || {}),
  } as UserData["class_properties"];

  const classDeckIds = Array.isArray(userClassProperties?.class_decks_id)
    ? [...userClassProperties.class_decks_id]
    : [];

  if (!classDeckIds.includes(deckId)) {
    classDeckIds.push(deckId);
    userClassProperties.class_decks_id = classDeckIds;

    await updateDoc(userDocRef, {
      class_properties: userClassProperties,
    });
  }

  // 3) Add user to class (deck document)
  const deckClassProperties = {
    ...(deckData.class_properties || {}),
  } as DeckObject["class_properties"];

  const idsOfUsersInClass = Array.isArray(deckClassProperties?.ids_of_users_in_class)
    ? [...deckClassProperties.ids_of_users_in_class]
    : [];

  if (!idsOfUsersInClass.includes(userId)) {
    idsOfUsersInClass.push(userId);
    deckClassProperties.ids_of_users_in_class = idsOfUsersInClass;

    await updateDoc(deckRef, {
      class_properties: deckClassProperties,
    });

    if (userDeckRef) {
      await updateDoc(userDeckRef, {
        class_properties: deckClassProperties,
      });
    }
  }
};

/**
 * Removes a user from a class (shared deck). Handles ownership transfer when the owner leaves
 * and there are other members, or deletes the class when the owner is the only member.
 */
export const leaveClassForUser = async (deckIdOfClass: string, user: UserData) => {
  if (!deckIdOfClass || !user) {
    return;
  }

  const deckRef = doc(db, DECK_COLLECTION_NAME, deckIdOfClass);
  const deckSnapshot = await getDoc(deckRef);
  const deckData = deckSnapshot.data() as DeckObject | undefined;

  if (!deckData) {
    return;
  }

  let currentDeckOwnerUserId: string | null = deckData.user_id ?? null;
  let userDeckRef: ReturnType<typeof doc> | null = null;

  if (currentDeckOwnerUserId) {
    userDeckRef = doc(db, `${USERS_COLLECTION_NAME}/${currentDeckOwnerUserId}/${DECK_COLLECTION_NAME}`, deckIdOfClass);
  }

  // Check if leaving user is the owner of the class
  const isOwner = currentDeckOwnerUserId === user?.id;

  // Get current users in class
  const deckClassProperties = { ...(deckData.class_properties || {}) } as DeckObject["class_properties"] & {
    deck_ids_in_class?: string[];
    class_ids_that_this_deck_belongs_to?: string[];
  };
  let idsOfUsersInClass: string[] = [...(deckClassProperties.ids_of_users_in_class || [])];

  // If owner is leaving, handle ownership transfer or deletion
  if (isOwner) {
    // Remove owner from the list to see who else is in the class
    const otherUsers = idsOfUsersInClass.filter((uid) => uid !== user?.id);

    if (otherUsers.length > 0) {
      // First, delete owner's subdecks before transferring ownership
      const subdecksQuery = query(
        collection(db, DECK_COLLECTION_NAME),
        where("parent_deck_id", "==", deckIdOfClass),
        where("user_id", "==", user?.id)
      );
      const subdecksSnapshot = await getDocs(subdecksQuery);
      for (const subdeckDoc of subdecksSnapshot.docs) {
        const subdeckData = subdeckDoc.data() as DeckObject;
        await deleteDeck({ id: subdeckDoc.id, ...subdeckData }, user);
      }

      // Transfer ownership to a random user
      const newOwnerId = otherUsers[Math.floor(Math.random() * otherUsers.length)];

      // Update the deck's user_id to the new owner
      await updateDoc(deckRef, { user_id: newOwnerId });

      // Also need to create/update the deck in the new owner's subcollection
      const newOwnerDeckRef = doc(db, `${USERS_COLLECTION_NAME}/${newOwnerId}/${DECK_COLLECTION_NAME}`, deckIdOfClass);

      // Copy the deck data to new owner's subcollection
      const deckDataCopy = { ...deckData, user_id: newOwnerId };
      await setDoc(newOwnerDeckRef, deckDataCopy);

      // Delete the deck from the old owner's subcollection
      if (userDeckRef) {
        await deleteDoc(userDeckRef);
      }

      // Update deck_data for rest of the function
      deckData.user_id = newOwnerId;
      currentDeckOwnerUserId = newOwnerId;
    } else {
      // Owner is the only one in the class - delete the class
      // First delete all subdecks
      const subdecksQuery = query(collection(db, DECK_COLLECTION_NAME), where("parent_deck_id", "==", deckIdOfClass));
      const subdecksSnapshot = await getDocs(subdecksQuery);
      for (const subdeckDoc of subdecksSnapshot.docs) {
        const subdeckData = subdeckDoc.data() as DeckObject;
        await deleteDeck({ id: subdeckDoc.id, ...subdeckData }, user);
      }

      // Delete the class deck
      await deleteDeck({ id: deckIdOfClass, ...deckData }, user);

      // Return early since deck is deleted
      return;
    }
  }

  // Continue with normal leave process for non-owners or after ownership transfer
  // 1) Remove class deck from user
  const userDocRef = doc(db, USERS_COLLECTION_NAME, user?.id);
  const userSnapshot = await getDoc(userDocRef);
  const userData = userSnapshot.data() as UserData | undefined;

  if (!userData) {
    return;
  }

  const userClassProperties = { ...(userData.class_properties || {}) } as UserData["class_properties"];
  let userClassDecksId = [...(userClassProperties.class_decks_id || [])];
  let deckIdsFromClassmatesThatHaveBeenAddedToUsersReviewSchedule = [
    ...(userClassProperties.deck_ids_from_classmates_that_have_been_added_to_users_review_schedule || []),
  ];

  if (userClassDecksId.includes(deckIdOfClass)) {
    userClassDecksId = userClassDecksId.filter((id) => id !== deckIdOfClass);
    userClassProperties.class_decks_id = userClassDecksId;
  }

  if (deckIdsFromClassmatesThatHaveBeenAddedToUsersReviewSchedule.includes(deckIdOfClass)) {
    deckIdsFromClassmatesThatHaveBeenAddedToUsersReviewSchedule =
      deckIdsFromClassmatesThatHaveBeenAddedToUsersReviewSchedule.filter((id) => id !== deckIdOfClass);
    userClassProperties.deck_ids_from_classmates_that_have_been_added_to_users_review_schedule =
      deckIdsFromClassmatesThatHaveBeenAddedToUsersReviewSchedule;
  }

  await updateDoc(userDocRef, { class_properties: userClassProperties });

  // 3) Remove user from deck class
  // Re-fetch if ownership was transferred
  if (isOwner && currentDeckOwnerUserId !== user?.id) {
    // ownership was transferred
    const deckSnapshotAfterTransfer = await getDoc(deckRef);
    const deckDataAfterTransfer = deckSnapshotAfterTransfer.data() as DeckObject;
    const deckClassPropertiesAfterTransfer = deckDataAfterTransfer?.class_properties || {};
    idsOfUsersInClass = [...(deckClassPropertiesAfterTransfer.ids_of_users_in_class || [])];
    deckClassProperties.ids_of_users_in_class = idsOfUsersInClass;
    Object.assign(deckClassProperties, deckClassPropertiesAfterTransfer);
  }

  if (idsOfUsersInClass.includes(user?.id)) {
    deckClassProperties.ids_of_users_in_class = idsOfUsersInClass.filter((id) => id !== user?.id);
  }

  // 5) remove class decks from user that user have added to the class
  const classesThisDeckBelongsToQuery = query(
    collection(db, DECK_COLLECTION_NAME),
    where("class_properties.class_ids_that_this_deck_belongs_to", "array-contains", deckIdOfClass),
    where("user_id", "==", user?.id)
  );
  const classesThisDeckBelongsToSnapshot = await getDocs(classesThisDeckBelongsToQuery);

  const deckIdsFromUser: string[] = [];
  for (const classmateDeckDoc of classesThisDeckBelongsToSnapshot.docs) {
    const classmateDeckData = classmateDeckDoc.data() as DeckObject & {
      class_properties?: { class_ids_that_this_deck_belongs_to?: string[] };
    };
    const classmateDeckId = classmateDeckDoc.id;
    deckIdsFromUser.push(classmateDeckId);
    const classmateDeckClassProperties = { ...(classmateDeckData.class_properties || {}) };
    let classmateClassIdsThatThisDeckBelongsTo = [
      ...(classmateDeckClassProperties.class_ids_that_this_deck_belongs_to || []),
    ];
    classmateClassIdsThatThisDeckBelongsTo = classmateClassIdsThatThisDeckBelongsTo.filter(
      (id) => id !== deckIdOfClass
    );
    classmateDeckClassProperties.class_ids_that_this_deck_belongs_to = classmateClassIdsThatThisDeckBelongsTo;

    await updateDoc(doc(db, DECK_COLLECTION_NAME, classmateDeckId), {
      class_properties: classmateDeckClassProperties,
    });

    const classmateDeckUserId = classmateDeckData.user_id;
    if (classmateDeckUserId) {
      const userClassmateDeckRef = doc(
        db,
        `${USERS_COLLECTION_NAME}/${classmateDeckUserId}/${DECK_COLLECTION_NAME}`,
        classmateDeckId
      );
      await setDoc(userClassmateDeckRef, { class_properties: classmateDeckClassProperties }, { merge: true });
    }
  }

  // 5) remove deck_ids from class_properties of class
  let deckIdsInClass = [...((deckClassProperties as { deck_ids_in_class?: string[] }).deck_ids_in_class || [])];
  for (const deckId of deckIdsFromUser) {
    deckIdsInClass = deckIdsInClass.filter((id) => id !== deckId);
  }
  (deckClassProperties as { deck_ids_in_class?: string[] }).deck_ids_in_class = deckIdsInClass;

  await updateDoc(deckRef, { class_properties: deckClassProperties });

  // Update user deck ref - need to handle ownership transfer case
  if (isOwner && currentDeckOwnerUserId !== user?.id) {
    // Ownership was transferred, update new owner's deck ref
    if (currentDeckOwnerUserId) {
      const newOwnerDeckRef = doc(
        db,
        `${USERS_COLLECTION_NAME}/${currentDeckOwnerUserId}/${DECK_COLLECTION_NAME}`,
        deckIdOfClass
      );
      await updateDoc(newOwnerDeckRef, { class_properties: deckClassProperties });
    }
  } else if (userDeckRef && !isOwner) {
    // Normal case - update original owner's deck ref
    await updateDoc(userDeckRef, { class_properties: deckClassProperties });
  }

  // 6) remove subdecks from user that user have added to the class
  // Skip if we're the owner - we already deleted subdecks above
  if (!isOwner) {
    const subdecksQuery = query(
      collection(db, DECK_COLLECTION_NAME),
      where("parent_deck_id", "==", deckIdOfClass),
      where("user_id", "==", user?.id)
    );
    const subdecksSnapshot = await getDocs(subdecksQuery);
    for (const subdeckDoc of subdecksSnapshot.docs) {
      const subdeckData = subdeckDoc.data() as DeckObject;
      await deleteDeck({ id: subdeckDoc.id, ...subdeckData }, user);
    }
  }

  // 6) Set deck as not class if no users are in the class
  const idsOfUsersInClassAfterRemoval = deckClassProperties.ids_of_users_in_class || [];
  if (idsOfUsersInClassAfterRemoval.length === 0) {
    await updateDoc(deckRef, { is_deck_a_class: false });
    // Update user deck ref - need to handle ownership transfer case
    if (isOwner && currentDeckOwnerUserId !== user?.id && currentDeckOwnerUserId) {
      // Ownership was transferred, update new owner's deck ref
      const newOwnerDeckRef = doc(
        db,
        `${USERS_COLLECTION_NAME}/${currentDeckOwnerUserId}/${DECK_COLLECTION_NAME}`,
        deckIdOfClass
      );
      await updateDoc(newOwnerDeckRef, { is_deck_a_class: false });
    } else if (userDeckRef && !isOwner) {
      // Normal case - update original owner's deck ref
      await updateDoc(userDeckRef, { is_deck_a_class: false });
    }
  }
};
